import React from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';

// Import all page components
import Home from './pages/Home';
import Login from './pages/Login';
import RegisterStep1 from './pages/RegisterStep1';
import RegisterStep2 from './pages/RegisterStep2';
import RegisterStep3 from './pages/RegisterStep3';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPasswordOTP from './pages/ResetPasswordOTP';
import NewPassword from './pages/NewPassword';
import PasswordResetSuccess from './pages/PasswordResetSuccess';
import Profile from './pages/Profile';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import Wishlist from './pages/Wishlist';
import Orders from './pages/Orders';
import Checkout from './pages/Checkout';
import OrderConfirmation from './pages/OrderConfirmation';
import Settings from './pages/Settings';
import Messages from './pages/Messages';
import SellerProducts from './pages/seller/Products';
import SellerDashboard from './pages/seller/Dashboard';
import SellerProductDetail from './pages/seller/SellerProductDetail';
import SellerAnalytics from './pages/seller/Analytics';
import AddProduct from './pages/seller/AddProduct';
import EditProduct from './pages/seller/EditProduct';
import AdminDashboard from './pages/admin/Dashboard';
import AdminUsers from './pages/admin/Users';
import AdminProducts from './pages/admin/Products';
import AdminOrders from './pages/admin/Orders';
import AdminLayout from './pages/admin/AdminLayout';
import AdminLogin from './pages/AdminLogin';
import ProfessionalSellers from './pages/admin/ProfessionalSellers';
import SellerVerification from './pages/admin/SellerVerification';
import AdminManagement from './pages/admin/AdminManagement';
import AdminChat from './pages/AdminChat';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CookiePolicy from './pages/CookiePolicy';
import Disclaimer from './pages/Disclaimer';
import AboutUs from './pages/AboutUs';
import FAQ from './pages/FAQ';
import ContactUs from './pages/ContactUs';
import NotFound from './pages/NotFound';
import Alerts from './pages/alerts/AlertsPage';

// Protected route component
const ProtectedRoute = ({ children, requiredRole }) => {
  const { currentUser, userRole, loading } = useAuth();
  const location = useLocation();

  const disableAdminGuard =
    requiredRole === 'admin' &&
    String(process.env.REACT_APP_DISABLE_ADMIN_GUARD).toLowerCase() === 'true';

  // Show loader while authentication is being checked
  if (loading) {
    return null; // Let AuthContext handle the loading state
  }

  if (disableAdminGuard) {
    return children;
  }

  if (!currentUser) {
    const loginPath = requiredRole === 'admin' ? '/admin/login' : '/login';
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }

  if (requiredRole === 'admin') {
    const isAdminOrStaff =
      userRole === 'admin' ||
      currentUser?.role === 'admin' ||
      currentUser?.is_staff ||
      currentUser?.is_superuser;

    if (!isAdminOrStaff) {
      return <Navigate to="/" replace />;
    }

    return children;
  }

  if (requiredRole && userRole && userRole !== requiredRole) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const AppRoutes = () => {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password-otp" element={<ResetPasswordOTP />} />
      <Route path="/new-password" element={<NewPassword />} />
      <Route path="/password-reset-success" element={<PasswordResetSuccess />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/register-step-1" element={<RegisterStep1 />} />
      <Route path="/register-step-2" element={<RegisterStep2 />} />
      <Route path="/register-step-3" element={<RegisterStep3 />} />
      <Route path="/register" element={<RegisterStep1 />} />
      <Route path="/products" element={<Products />} />
      <Route path="/products/:id" element={<ProductDetail />} />
      
      {/* Legal and info pages */}
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/cookies" element={<CookiePolicy />} />
      <Route path="/disclaimer" element={<Disclaimer />} />
      <Route path="/about" element={<AboutUs />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/contact" element={<ContactUs />} />
      <Route path="/alerts" element={<Alerts />} />
      
      {/* Buyer routes */}
      <Route path="/cart" element={
        <ProtectedRoute requiredRole="buyer">
          <Cart />
        </ProtectedRoute>
      } />
      <Route path="/wishlist" element={
        <ProtectedRoute requiredRole="buyer">
          <Wishlist />
        </ProtectedRoute>
      } />
      <Route path="/buyer-orders" element={
        <ProtectedRoute requiredRole="buyer">
          <Orders />
        </ProtectedRoute>
      } />
      <Route path="/checkout" element={
        <ProtectedRoute requiredRole="buyer">
          <Checkout />
        </ProtectedRoute>
      } />
      <Route path="/order-confirmation/:orderId" element={
        <ProtectedRoute>
          <OrderConfirmation />
        </ProtectedRoute>
      } />
      
      {/* Seller routes */}
      <Route path="/seller/dashboard" element={
        <ProtectedRoute requiredRole="seller">
          <SellerDashboard />
        </ProtectedRoute>
      } />
      <Route path="/seller/analytics" element={
        <ProtectedRoute requiredRole="seller">
          <SellerAnalytics />
        </ProtectedRoute>
      } />
      <Route path="/seller/products" element={
        <ProtectedRoute requiredRole="seller">
          <SellerProducts />
        </ProtectedRoute>
      } />
      <Route path="/seller/products/:id" element={
        <ProtectedRoute requiredRole="seller">
          <SellerProductDetail />
        </ProtectedRoute>
      } />
      <Route path="/seller/add-product" element={
        <ProtectedRoute requiredRole="seller">
          <AddProduct />
        </ProtectedRoute>
      } />
      <Route path="/seller/edit-product/:id" element={
        <ProtectedRoute requiredRole="seller">
          <EditProduct />
        </ProtectedRoute>
      } />
      
      {/* Common authenticated routes */}
      <Route path="/profile" element={
        <ProtectedRoute>
          <Profile />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <Settings />
        </ProtectedRoute>
      } />
      <Route path="/messages" element={
        <ProtectedRoute>
          <Messages />
        </ProtectedRoute>
      } />
      <Route path="/messages/:conversationId" element={
        <ProtectedRoute>
          <Messages />
        </ProtectedRoute>
      } />
      
      {/* Admin login route - outside protection */}
      <Route path="/admin/login" element={<AdminLogin />} />
      
      {/* Admin routes */}
      <Route element={
        <ProtectedRoute requiredRole="admin">
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="/admin/users" element={<AdminUsers />} />
        <Route path="/admin/products" element={<AdminProducts />} />
        <Route path="/admin/orders" element={<AdminOrders />} />
        <Route path="/admin/professional-sellers" element={<ProfessionalSellers />} />
        <Route path="/admin/verify-sellers" element={<SellerVerification />} />
        <Route path="/admin/management" element={<AdminManagement />} />
        <Route path="/admin/admin-management" element={<AdminManagement />} />
        <Route path="/admin/chat" element={<AdminChat />} />
      </Route>
      
      {/* 404 route */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default AppRoutes;
